import React, { useState } from 'react';
import { Album } from '../../types/album';
import { theme } from '../../styles/theme';
import { itunesService } from '../../services/itunesService';
import { secureSupabaseService } from '../../services/secureSupabaseService';

interface ITunesCollectionModalProps {
  albums: Album[];
  onClose: () => void;
  onAlbumsUpdated: (albums: Album[]) => void;
}

interface ITunesProgress {
  stage: 'analyzing' | 'processing' | 'complete';
  current: number;
  total: number;
  currentAlbum: string;
  added: number;
  skipped: number;
  errors: string[];
}

const ITunesCollectionModal: React.FC<ITunesCollectionModalProps> = ({
  albums,
  onClose,
  onAlbumsUpdated
}) => {
  const [isEnhancing, setIsEnhancing] = useState(false);
  const [progress, setProgress] = useState<ITunesProgress | null>(null);

  const handleEnhance = async (fullRefresh: boolean = false) => {
    if (!itunesService.isConfigured()) {
      alert('iTunes service not available.');
      return;
    }

    // Filter albums based on whether we're doing full refresh or just adding missing previews
    const albumsToProcess = albums.filter(album => {
      if (!album.tracklist || album.tracklist.length === 0) return false;
      
      if (fullRefresh) {
        return true; // Process all albums with tracklists
      } else {
        // Only process albums that don't have any previews yet
        return !album.tracklist.some(track => track.previewUrl);
      }
    });

    if (albumsToProcess.length === 0) {
      const message = fullRefresh 
        ? 'No albums with tracklists found to refresh.'
        : 'No albums with tracklists found to enhance.';
      alert(message);
      return;
    }

    setIsEnhancing(true);
    const updatedAlbums = [...albums];
    let added = 0;
    let skipped = 0;
    const errors: string[] = [];

    setProgress({
      stage: 'analyzing',
      current: 0,
      total: albumsToProcess.length,
      currentAlbum: fullRefresh ? 'Refreshing all previews...' : 'Analyzing collection...',
      added: 0,
      skipped: 0,
      errors: []
    });

    try {
      for (let i = 0; i < albumsToProcess.length; i++) {
        const album = albumsToProcess[i];
        
        setProgress(prev => prev ? {
          ...prev,
          stage: 'processing',
          current: i + 1,
          currentAlbum: `${album.artist} - ${album.title}`,
        } : null);

        try {
          // Check if album needs enhancement
          if (fullRefresh) {
            // For full refresh, clear existing preview data first
            if (album.tracklist) {
              album.tracklist.forEach(track => {
                delete track.previewUrl;
                delete track.itunesId;
              });
            }
            console.log(`${album.artist} - ${album.title}: Clearing existing previews for full refresh`);
          } else {
            // For incremental, check if album needs enhancement
            const tracksWithoutPreviews = album.tracklist?.filter(track => !track.previewUrl).length || 0;
            const totalTracks = album.tracklist?.length || 0;
            
            console.log(`${album.artist} - ${album.title}: ${totalTracks} tracks, ${totalTracks - tracksWithoutPreviews} with previews, ${tracksWithoutPreviews} without`);
            
            if (tracksWithoutPreviews === 0) {
              // All tracks already have previews
              skipped++;
              setProgress(prev => prev ? { ...prev, skipped } : null);
              continue;
            }
          }

          const enhancedTracks = await itunesService.enhanceTracksWithPreviews(
            album.tracklist!,
            album.artist,
            album.title,
            () => {} // No per-track progress needed here
          );

          // Small delay between albums to be extra safe with rate limiting
          await new Promise(resolve => setTimeout(resolve, 500));

          const tracksWithNewPreviews = enhancedTracks.filter(track => track.previewUrl).length;
          const originalPreviews = album.tracklist?.filter(track => track.previewUrl).length || 0;

          console.log(`${album.artist} - ${album.title}: Enhanced ${tracksWithNewPreviews} tracks (was ${originalPreviews})`);

          if (tracksWithNewPreviews > originalPreviews) {
            // Update the album in our array
            const albumIndex = updatedAlbums.findIndex(a => a.id === album.id);
            if (albumIndex !== -1) {
              updatedAlbums[albumIndex] = {
                ...updatedAlbums[albumIndex],
                tracklist: enhancedTracks
              };
            }

            // Save to storage
            await secureSupabaseService.saveAlbum(updatedAlbums[albumIndex]);
            added++;
          } else {
            skipped++;
          }

        } catch (error) {
          const errorMsg = `${album.artist} - ${album.title}: ${error instanceof Error ? error.message : 'Unknown error'}`;
          errors.push(errorMsg);
          console.error(`Failed to enhance ${album.artist} - ${album.title}:`, error);
        }

        setProgress(prev => prev ? {
          ...prev,
          added,
          skipped,
          errors
        } : null);
      }

      setProgress(prev => prev ? {
        ...prev,
        stage: 'complete',
        currentAlbum: 'Enhancement complete!'
      } : null);

      // Update parent component
      onAlbumsUpdated(updatedAlbums);

    } catch (error) {
      console.error('Collection enhancement failed:', error);
      errors.push(`Collection enhancement failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsEnhancing(false);
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: theme.zIndex.modal,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.xl,
    maxWidth: '600px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    position: 'relative',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#1db954', // Spotify green
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: `all ${theme.transitions.fast}`,
    marginRight: theme.spacing.sm,
  };

  const closeButtonStyle: React.CSSProperties = {
    position: 'absolute',
    top: theme.spacing.md,
    right: theme.spacing.md,
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: theme.colors.textMuted,
    cursor: 'pointer',
  };

  const progressStyle: React.CSSProperties = {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.md,
  };

  const progressBarStyle: React.CSSProperties = {
    width: '100%',
    height: '8px',
    backgroundColor: theme.colors.surfaceLight,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
    marginBottom: theme.spacing.sm,
  };

  const progressFillStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: '#1db954',
    width: `${progress ? (progress.current / progress.total) * 100 : 0}%`,
    transition: 'width 0.3s ease',
  };

  const albumsWithTracklists = albums.filter(album => 
    album.tracklist && album.tracklist.length > 0
  ).length;

  const albumsWithoutPreviews = albums.filter(album => 
    album.tracklist && album.tracklist.length > 0 &&
    !album.tracklist.some(track => track.previewUrl)
  ).length;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>×</button>
        
        <div style={titleStyle}>Add iTunes Previews to Collection</div>
        
        <div style={{ color: theme.colors.textSecondary, marginBottom: theme.spacing.lg }}>
          <strong>Collection Overview:</strong>
          <br />
          {albums.length} total albums
          <br />
          {albumsWithTracklists} albums with tracklists
          <br />
          {albumsWithoutPreviews} albums without any previews
          <br />
          {albumsWithTracklists - albumsWithoutPreviews} albums with some/all previews
        </div>

        {!itunesService.isConfigured() && (
          <div style={{
            padding: theme.spacing.md,
            backgroundColor: `${theme.colors.warning}20`,
            borderRadius: theme.borderRadius.md,
            marginBottom: theme.spacing.lg,
            fontSize: '0.875rem',
            color: theme.colors.warning
          }}>
            <strong>iTunes service not available</strong><br />
            The iTunes Search API service is currently unavailable.
          </div>
        )}

        {progress && (
          <div style={progressStyle}>
            <div style={progressBarStyle}>
              <div style={progressFillStyle} />
            </div>
            <div style={{ fontSize: '0.875rem', color: theme.colors.text, marginBottom: theme.spacing.xs }}>
              {progress.current} of {progress.total} • {progress.currentAlbum}
            </div>
            <div style={{ fontSize: '0.75rem', color: theme.colors.textMuted }}>
              Previews added: {progress.added} • Skipped: {progress.skipped}
            </div>
            
            {progress.errors.length > 0 && (
              <div style={{
                marginTop: theme.spacing.sm,
                padding: theme.spacing.sm,
                backgroundColor: `${theme.colors.error}20`,
                borderRadius: theme.borderRadius.sm,
                maxHeight: '100px',
                overflow: 'auto'
              }}>
                <div style={{ fontSize: '0.75rem', color: theme.colors.error, fontWeight: '600' }}>
                  Errors ({progress.errors.length}):
                </div>
                {progress.errors.slice(-3).map((error, i) => (
                  <div key={i} style={{ fontSize: '0.75rem', color: theme.colors.error }}>
                    {error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: theme.spacing.sm, marginTop: theme.spacing.lg, flexWrap: 'wrap' }}>
          {/* Add Missing Previews Button */}
          <button
            style={{
              ...buttonStyle,
              backgroundColor: '#007AFF', // iTunes blue
              opacity: isEnhancing || !albumsWithoutPreviews || !itunesService.isConfigured() ? 0.5 : 1,
            }}
            onClick={() => handleEnhance(false)}
            disabled={isEnhancing || !albumsWithoutPreviews || !itunesService.isConfigured()}
          >
            {isEnhancing ? 'Processing...' : `Add Missing (${albumsWithoutPreviews} albums)`}
          </button>
          
          {/* Refresh All Previews Button */}
          <button
            style={{
              ...buttonStyle,
              backgroundColor: '#FF6B35', // Orange for refresh action
              opacity: isEnhancing || !albumsWithTracklists || !itunesService.isConfigured() ? 0.5 : 1,
            }}
            onClick={() => handleEnhance(true)}
            disabled={isEnhancing || !albumsWithTracklists || !itunesService.isConfigured()}
            title="Clear all existing previews and refresh from scratch"
          >
            {isEnhancing ? 'Processing...' : `Refresh All (${albumsWithTracklists} albums)`}
          </button>
          
          <button
            style={{
              ...buttonStyle,
              backgroundColor: theme.colors.surface,
              color: theme.colors.text,
            }}
            onClick={onClose}
          >
            {progress?.stage === 'complete' ? 'Done' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ITunesCollectionModal;